import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sidebar } from './Sidebar';
import { MobileNav } from './MobileNav';
import { AccountSwitcherDropdown, ProfileButton, type AccountsState } from '../../accounts';
import { DemoBanner } from '../shared/DemoBanner';
import { FloatingDjay } from '../shared/FloatingDjay';
import { AchievementCeremony } from '../shared/AchievementCeremony';
import { Menu } from 'lucide-react';
import { APP_NAME } from '../../config/constants';
import { useAppStore } from '../../app/hooks/useAppStore';
interface AppShellProps {
  children: React.ReactNode;
  currentView: string;
  onNavigate: (view: string) => void;
  accounts?: AccountsState;
  onSwitchAccount?: (accountId: string) => void;
  onLogout?: () => void;
  onResetDemo?: () => void;
}
export function AppShell({
  children,
  currentView,
  onNavigate,
  accounts,
  onSwitchAccount,
  onLogout,
  onResetDemo
}: AppShellProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
  const {
    isDemoMode
  } = useAppStore();
  // Auto-collapse sidebar on tablet screens (768px - 1023px)
  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    const handleResize = (e: MediaQueryListEvent | MediaQueryList) => {
      if (e.matches) {
        setIsSidebarCollapsed(true);
      }
    };
    // Initial check
    handleResize(mediaQuery);
    // Listen for changes
    mediaQuery.addEventListener('change', handleResize);
    return () => mediaQuery.removeEventListener('change', handleResize);
  }, []);
  // Track if this is the first render to skip the entry animation
  const isFirstRender = useRef(true);
  const skipInitial = isFirstRender.current;
  if (isFirstRender.current) {
    isFirstRender.current = false;
  }
  // Convert AccountsState to array of accounts
  const accountsList = accounts ? Object.values(accounts.accountsById) : [];
  const activeAccountId = accounts?.activeAccountId || '';
  const activeAccount = accounts ? accounts.accountsById[activeAccountId] : null;
  return <div className="min-h-screen bg-[#FAFAF8] flex flex-col font-sans text-slate-900" data-id="element-1291">
      {/* Demo Banner */}
      {isDemoMode && onResetDemo && <DemoBanner onResetDemo={onResetDemo} onRegister={() => onNavigate('register')} data-id="element-1292" />}

      <div className="flex-1 flex overflow-hidden" data-id="element-1293">
        {/* Skip-to-content link for keyboard accessibility */}
        <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-50 focus:px-4 focus:py-2 focus:bg-orange-500 focus:text-white focus:rounded-lg focus:shadow-lg focus:outline-none focus:ring-2 focus:ring-orange-300 font-semibold text-sm" data-id="element-1294">
          Ga naar inhoud
        </a>

        {/* Desktop Sidebar */}
        <Sidebar currentView={currentView} onNavigate={onNavigate} isCollapsed={isSidebarCollapsed} onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)} data-id="element-1295" />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col min-w-0 h-full overflow-hidden relative" data-id="element-1296">
          {/* Mobile Header */}
          <header className="md:hidden h-20 bg-white/80 backdrop-blur-md border-b border-slate-200/60 flex items-center justify-between px-5 sticky top-0 z-30 shadow-sm" data-id="element-1297">
            <div className="flex items-center gap-3" data-id="element-1298">
              <button onClick={() => setIsMobileNavOpen(true)} className="p-3 -ml-2 text-slate-600 hover:bg-orange-50 hover:text-orange-600 rounded-xl transition-colors min-h-[48px] min-w-[48px] flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-orange-300" aria-label="Menu openen" data-id="element-1299">
                <Menu className="w-6 h-6" data-id="element-1300" />
              </button>
              <span className="font-black text-slate-800 text-lg tracking-tight" data-id="element-1301">
                {APP_NAME}
              </span>
            </div>

            <div className="flex items-center gap-3" data-id="element-1302">
              {/* Mobile Account Switcher / Profile Button */}
              {isDemoMode ? accountsList.length > 0 && onSwitchAccount && <AccountSwitcherDropdown accounts={accountsList} activeAccountId={activeAccountId} onSwitchAccount={onSwitchAccount} data-id="element-1303" /> : activeAccount && onLogout && <ProfileButton account={activeAccount} onNavigate={onNavigate} onLogout={onLogout} data-id="element-1304" />}
            </div>
          </header>

          {/* Desktop Header */}
          <header className="hidden md:flex h-20 bg-white/80 backdrop-blur-md border-b border-slate-200/60 items-center justify-end px-8 sticky top-0 z-30 shadow-sm" data-id="element-1305">
            {isDemoMode ? <div className="flex items-center gap-3" data-id="element-1306">
                <button onClick={() => onNavigate('register')} className="flex items-center gap-1.5 px-4 py-2 bg-orange-50 text-orange-600 hover:bg-orange-100 rounded-xl text-sm font-bold transition-colors" data-id="element-1307">
                  🧪 Start Registratie
                </button>
                {accountsList.length > 0 && onSwitchAccount && <AccountSwitcherDropdown accounts={accountsList} activeAccountId={activeAccountId} onSwitchAccount={onSwitchAccount} data-id="element-1308" />}
              </div> : activeAccount && onLogout && <ProfileButton account={activeAccount} onNavigate={onNavigate} onLogout={onLogout} data-id="element-1309" />}
          </header>

          {/* Scrollable Content */}
          <main id="main-content" className="flex-1 overflow-y-auto overflow-x-hidden p-6 md:p-10 scroll-smooth" data-id="element-1310">
            <div className="max-w-6xl mx-auto w-full pb-24 md:pb-12" data-id="element-1311">
              <AnimatePresence mode="wait" data-id="element-1312">
                <motion.div key={currentView} initial={skipInitial ? false : {
                opacity: 0,
                y: 10,
                scale: 0.99
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -10,
                scale: 0.99
              }} transition={{
                duration: 0.2,
                ease: 'easeOut'
              }} className="w-full" data-id="element-1313">
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>

        {/* Mobile Navigation Drawer */}
        <MobileNav isOpen={isMobileNavOpen} onClose={() => setIsMobileNavOpen(false)} currentView={currentView} onNavigate={onNavigate} onLogout={onLogout} data-id="element-1314" />
      </div>

      {/* Floating Djay Buddy */}
      <FloatingDjay currentView={currentView} data-id="element-1315" />

      {/* Global Achievement Ceremony */}
      <AchievementCeremony data-id="element-1316" />
    </div>;
}